// PageActions.js
// Renderiza apenas os botões page-specific (TitleBar.buttons via context),
// CENTRALIZADOS, no topo do conteúdo da page — sempre logo acima do
// elemento de Buscar (que pages renderizam dentro do MainContainer).
//
// Substitui o antigo PageHeader (removido em 2026-05-10). Breadcrumbs e
// título foram movidos pra TopActionBar; o título textual deixou de
// existir como elemento separado (o breadcrumb já cumpre esse papel
// semântico via aria-current="page" no leaf).
//
// IMPORTANTE — padronização visual (2026-05-10, decisão Filipe):
// Este componente IGNORA `button.style` e `button.className` passados pelas
// pages. O motivo: cada page passava style inline com margens/paddings
// próprios, gerando visual descoordenado (alturas diferentes, espaçamentos
// inconsistentes, border-radius variável). Aqui aplicamos sx fixo:
//   - pill (border-radius 999)
//   - altura ~40px (size="small" do MUI, ajustado via padding)
//   - laranja Zoetis (primary.main)
//   - gap consistente entre botões
//   - flex-wrap pra mobile
//
// Continuam respeitados do contrato button: label, icon, onClick, disabled,
// hasDropdown, buttons (subdropdown).

import React from 'react'
import { Box, Menu, MenuItem, ListItemText } from '@mui/material'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import { usePageLayout } from './PageLayoutProvider'
import AppButton from '../material/AppButton'

function StandardActionButton({ button }) {
  return (
    <AppButton
      variant="primary"
      onClick={button.onClick}
      startIcon={button.icon}
      disabled={button.disabled}
    >
      {button.label}
    </AppButton>
  )
}

function StandardDropdownButton({ button }) {
  const [anchorEl, setAnchorEl] = React.useState(null)
  const open = Boolean(anchorEl)
  return (
    <>
      <AppButton
        variant="primary"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        startIcon={button.icon}
        endIcon={<ArrowDropDownIcon />}
        disabled={button.disabled}
      >
        {button.label}
      </AppButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {(button.buttons || []).map((sub) => (
          <MenuItem
            key={sub.label}
            onClick={() => {
              setAnchorEl(null)
              sub.onClick && sub.onClick()
            }}
          >
            <ListItemText primary={sub.label} />
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

export default function PageActions() {
  const { pageInfo } = usePageLayout()
  const buttons = pageInfo?.buttons
  if (!buttons || buttons.length === 0) return null

  return (
    <Box
      component="section"
      aria-label="Ações da página"
      sx={{
        pt: { xs: 2, md: 2.5 },
        pb: { xs: 1, md: 1.5 },
        '@media print': { display: 'none' },
      }}
    >
      {/* Inner wrapper constrained — alinha com AppContent (max 1440px).
          Outer Box fica full-width; o padding e alinhamento vivem aqui.
          Box com sx flex+gap em vez de Stack — a prop `gap` do Stack do MUI
          NÃO é repassada pro sx; só `spacing` é canonical. */}
      <Box
        sx={{
          width: '100%',
          maxWidth: 1440,
          mx: 'auto',
          px: { xs: 2, md: 3 },
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          // 2026-05-11 (Filipe F6): right-aligned em vez de center — fica
          // visualmente abaixo dos pills da TopActionBar (Sync/Backup) que
          // também são right-aligned.
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: { xs: 1.5, md: 2 }, // 12px mobile / 16px desktop
        }}
      >
        {buttons.map((button) =>
          button.hasDropdown ? (
            <StandardDropdownButton key={button.label} button={button} />
          ) : (
            <StandardActionButton key={button.label} button={button} />
          )
        )}
      </Box>
    </Box>
  )
}
